import { z } from 'zod';
import { SESSION_STATUSES } from '@bulk/types';
import {
  dateOnlySchema,
  dateTimeSchema,
  idSchema,
  paginationQuerySchema,
  patchOf,
  publicAssetSchema,
  timeHHMM,
  weekdaySchema,
} from './common';

export const trainerSpecialtyTagSchema = z.object({
  id: idSchema,
  key: z.string(),
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
});
export type TrainerSpecialtyTag = z.infer<typeof trainerSpecialtyTagSchema>;

export const createTrainerSchema = z.object({
  firstName: z.string().trim().min(1).max(80),
  lastName: z.string().trim().min(1).max(80),
  email: z.email().toLowerCase().nullable().optional(),
  phone: z.string().trim().max(32).nullable().optional(),
  specialtyIds: z.array(idSchema).max(20).default([]).describe("Ids from the gym's speciality list (GET /trainers/specialties); free text is not accepted"),
  photoAssetId: idSchema.nullable().optional().describe('The staff photo; the website shows it until a website photo is set'),
  userId: idSchema.nullable().optional(),
});
export type CreateTrainerInput = z.infer<typeof createTrainerSchema>;
export const updateTrainerSchema = patchOf(createTrainerSchema).extend({
  active: z.boolean().optional(),
});
export type UpdateTrainerInput = z.infer<typeof updateTrainerSchema>;

const trainerProfileFields = z.object({
  sitePhotoAssetId: idSchema.nullable().describe('Shown on the website instead of the staff photo; null falls back to it'),
  firstNameLocalized: z.record(z.string(), z.string().trim().max(80)).describe('The first name per locale; an empty or missing one shows the roster name'),
  lastNameLocalized: z.record(z.string(), z.string().trim().max(80)),
  bioLocalized: z.record(z.string(), z.string().trim().max(3000)),
  isPublic: z.boolean().describe('Shown on the website'),
});
export const updateTrainerProfileSchema = patchOf(trainerProfileFields);
export type UpdateTrainerProfileInput = z.infer<typeof updateTrainerProfileSchema>;

export const trainerOrderSchema = z.object({
  trainerIds: z.array(idSchema).max(1000).describe('Every active trainer exactly once, in website order'),
});
export type TrainerOrderInput = z.infer<typeof trainerOrderSchema>;

export const trainerSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  userId: idSchema.nullable(),
  firstName: z.string(),
  lastName: z.string(),
  firstNameLocalized: z.record(z.string(), z.string()),
  lastNameLocalized: z.record(z.string(), z.string()),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  bio: z.string().nullable().describe('Legacy single-language bio the website falls back to; saving bioLocalized through the profile clears it'),
  bioLocalized: z.record(z.string(), z.string()),
  specialties: z.array(z.string()).describe("English names of the linked specialities, in the gym's order"),
  specialtyIds: z.array(idSchema),
  specialtyTags: z.array(trainerSpecialtyTagSchema),
  photoAssetId: idSchema.nullable(),
  photo: publicAssetSchema.nullable(),
  sitePhotoAssetId: idSchema.nullable(),
  sitePhoto: publicAssetSchema.nullable(),
  isPublic: z.boolean(),
  active: z.boolean(),
  sortOrder: z.number().int().describe('Position on the website, 1 first'),
  createdAt: z.string(),
});
export type TrainerDto = z.infer<typeof trainerSchema>;

export const createTrainerSpecialtySchema = z.object({
  name: z.string().trim().min(1).max(60),
  nameLocalized: z.record(z.string(), z.string().trim().max(60)).default({}),
  sortOrder: z.number().int().min(0).max(10_000).optional().describe('Omitted: after the last one'),
  active: z.boolean().default(true),
});
export type CreateTrainerSpecialtyInput = z.infer<typeof createTrainerSpecialtySchema>;
export const updateTrainerSpecialtySchema = patchOf(createTrainerSpecialtySchema);
export type UpdateTrainerSpecialtyInput = z.infer<typeof updateTrainerSpecialtySchema>;

export const trainerSpecialtySchema = trainerSpecialtyTagSchema.extend({
  gymId: idSchema,
  sortOrder: z.number().int(),
  active: z.boolean(),
  trainerCount: z.number().int(),
  createdAt: z.string(),
});
export type TrainerSpecialtyDto = z.infer<typeof trainerSpecialtySchema>;

export const createClassSchema = z.object({
  name: z.string().trim().min(2).max(120),
  nameLocalized: z.record(z.string(), z.string()).default({}),
  description: z.string().trim().max(3000).nullable().default(null),
  defaultTrainerId: idSchema.nullable().default(null),
  defaultCapacity: z.number().int().min(1).max(500).default(20),
  defaultDurationMinutes: z.number().int().min(5).max(600).default(60),
  defaultRoomId: idSchema.nullable().default(null).describe("A room of any branch; used by the class's slots and sessions at that branch that name none"),
  requiresMembership: z.boolean().default(true),
  allowedPlanIds: z.array(idSchema).default([]),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#f2c94c'),
  isPublic: z.boolean().default(true),
});
export type CreateClassInput = z.infer<typeof createClassSchema>;
export const updateClassSchema = patchOf(createClassSchema).extend({
  active: z.boolean().optional(),
});
export type UpdateClassInput = z.infer<typeof updateClassSchema>;

export const classSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  description: z.string().nullable(),
  defaultTrainerId: idSchema.nullable(),
  defaultCapacity: z.number().int(),
  defaultDurationMinutes: z.number().int(),
  defaultRoomId: idSchema.nullable(),
  location: z.string().nullable().describe('The name of the default room'),
  requiresMembership: z.boolean(),
  allowedPlanIds: z.array(idSchema),
  color: z.string(),
  isPublic: z.boolean(),
  active: z.boolean(),
  createdAt: z.string(),
  trainerPhoto: publicAssetSchema.nullable().describe("The default trainer's photo"),
});
export type ClassDto = z.infer<typeof classSchema>;

export const createScheduleSchema = z.object({
  classId: idSchema,
  weekday: weekdaySchema,
  startTime: timeHHMM,
  durationMinutes: z.number().int().min(5).max(600).optional(),
  trainerId: idSchema.nullable().optional(),
  capacity: z.number().int().min(1).max(500).optional(),
  roomId: idSchema.nullable().optional().describe("A room of the slot's branch; the class's default room there when omitted"),
  locationId: idSchema.optional().describe('The branch; the one in X-Location-Id when omitted'),
  validFrom: dateOnlySchema.optional(),
  validUntil: dateOnlySchema.nullable().optional(),
});
export type CreateScheduleInput = z.infer<typeof createScheduleSchema>;

export const scheduleSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  classId: idSchema,
  weekday: weekdaySchema,
  startTime: z.string(),
  durationMinutes: z.number().int().nullable(),
  trainerId: idSchema.nullable(),
  capacity: z.number().int().nullable(),
  roomId: idSchema.nullable(),
  location: z.string().nullable().describe("The room's name"),
  locationId: idSchema.describe('The branch its sessions happen at'),
  validFrom: z.string(),
  validUntil: z.string().nullable(),
  active: z.boolean(),
});
export type ScheduleDto = z.infer<typeof scheduleSchema>;

export const createSessionSchema = z.object({
  classId: idSchema,
  startsAt: dateTimeSchema,
  durationMinutes: z.number().int().min(5).max(600).optional(),
  trainerId: idSchema.nullable().optional(),
  capacity: z.number().int().min(1).max(500).optional(),
  roomId: idSchema.nullable().optional().describe("A room of the session's branch; the class's default room there when omitted"),
  locationId: idSchema.optional().describe('The branch; the one in X-Location-Id when omitted'),
});
export type CreateSessionInput = z.infer<typeof createSessionSchema>;

export const updateSessionSchema = z.object({
  startsAt: dateTimeSchema.optional(),
  durationMinutes: z.number().int().min(5).max(600).optional(),
  trainerId: idSchema.nullable().optional(),
  capacity: z.number().int().min(1).max(500).optional(),
  roomId: idSchema.nullable().optional().describe("A room of the session's branch; moving to another branch without one clears the room"),
  locationId: idSchema.optional().describe('Move the session to another branch'),
  status: z.enum(SESSION_STATUSES).optional(),
});
export type UpdateSessionInput = z.infer<typeof updateSessionSchema>;

export const bookingRefusalSchema = z.object({
  code: z.string(),
  details: z.record(z.string(), z.unknown()).nullable(),
});
export type BookingRefusal = z.infer<typeof bookingRefusalSchema>;

export const sessionSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  classId: idSchema,
  className: z.string(),
  classColor: z.string(),
  scheduleId: idSchema.nullable(),
  trainerId: idSchema.nullable(),
  trainerName: z.string().nullable(),
  trainerPhoto: publicAssetSchema.nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  capacity: z.number().int(),
  bookedCount: z.number().int(),
  waitlistCount: z.number().int(),
  roomId: idSchema.nullable(),
  location: z.string().nullable().describe('The name of the room inside the branch'),
  locationId: idSchema.describe('The branch the session happens at'),
  locationName: z.string(),
  status: z.enum(SESSION_STATUSES),
  myBookingId: idSchema.nullable().optional(),
  myBookingStatus: z.string().nullable().optional(),
  myRefusal: bookingRefusalSchema.nullable().optional().describe("For a member: why booking this session would be refused (the code createBooking answers with); null when it can be booked"),
});
export type SessionDto = z.infer<typeof sessionSchema>;

export const sessionListQuerySchema = paginationQuerySchema.extend({
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
  classId: idSchema.optional(),
  trainerId: idSchema.optional(),
  locationId: idSchema.optional().describe('Only this branch; staff lists already follow X-Location-Id'),
  status: z.enum(SESSION_STATUSES).optional(),
});
export type SessionListQuery = z.infer<typeof sessionListQuerySchema>;

export const generateSessionsSchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
});
export type GenerateSessionsInput = z.infer<typeof generateSessionsSchema>;
